import { useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { useDeleteEquipmentImage, useEquipmentImages, useUploadEquipmentImage } from "@/hooks/use-equipment-media";

const MAX_BYTES = 50 * 1024 * 1024;

/**
 * Egne bilder av utstyret. Uten egne bilder vises produsentbildet fra modellen
 * (VIB-2918); egne bilder foretrekkes alltid som hovedbilde.
 */
export function EquipmentImages({
  equipmentId,
  name,
  canManage,
  productImageUrl,
}: {
  equipmentId: string;
  name: string;
  canManage: boolean;
  productImageUrl?: string | null;
}) {
  const { data: images = [], isLoading } = useEquipmentImages(equipmentId);
  const upload = useUploadEquipmentImage(equipmentId);
  const remove = useDeleteEquipmentImage(equipmentId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [main, ...rest] = images;

  const onFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Velg en bildefil");
    if (file.size > MAX_BYTES) return toast.error("Bildet er større enn 50 MB");
    upload.mutate(file, {
      onSuccess: () => toast.success("Bildet er lastet opp"),
      onError: (e) => toast.error((e as Error).message),
    });
  };

  return (
    <Card>
      <CardContent className="pt-6 space-y-3">
        {main ? (
          <img src={main.url} alt={name} className="w-full aspect-video object-contain rounded-md bg-muted" />
        ) : productImageUrl ? (
          <figure className="space-y-1">
            <img src={productImageUrl} alt={`${name}, produsentbilde`} className="w-full aspect-video object-contain rounded-md bg-muted" />
            <figcaption className="text-xs text-muted-foreground">Produsentbilde</figcaption>
          </figure>
        ) : (
          <div className="w-full aspect-video rounded-md bg-muted flex flex-col items-center justify-center text-sm text-muted-foreground gap-2">
            <SrIcon name="image" className="h-7 w-7" />
            {isLoading ? "Laster bilder …" : "Ingen bilder ennå"}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {rest.map((img, i) => (
            <img key={img.path} src={img.url} alt={`${name}, bilde ${i + 2}`} className="h-14 w-[72px] object-cover rounded" />
          ))}
          {canManage && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                aria-label="Velg bilde å laste opp"
                onChange={(e) => {
                  onFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={upload.isPending}>
                <SrIcon name="upload" className="h-4 w-4" />
                {upload.isPending ? "Laster opp …" : "Last opp eget bilde"}
              </Button>
              {main && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (!confirm("Fjerne hovedbildet?")) return;
                    remove.mutate(main.path, { onError: (e) => toast.error((e as Error).message) });
                  }}
                >
                  Fjern bildet
                </Button>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
